import React, { useLayoutEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@auth/AuthContext";
import { setRedirectAfterLogin } from "@auth/redirectAfterLogin";

function AuthGuard({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // LWI-592 — stash the original deep link in sessionStorage so it survives
  // multi-step auth (2FA, tenant picker, SSO). Includes search + hash so
  // query strings and anchors round-trip correctly. useLayoutEffect runs
  // synchronously in the commit phase before the Navigate component's
  // effect fires the route change, so the stash is in place by the time
  // Login mounts. Router state still carries `from` as a fallback for the
  // single-step SPA-internal case.
  useLayoutEffect(() => {
    if (!loading && !isAuthenticated) {
      setRedirectAfterLogin(
        location.pathname + (location.search || "") + (location.hash || "")
      );
    }
  }, [loading, isAuthenticated, location.pathname, location.search, location.hash]);

  if (loading) return null; // or a splash screen

  if (!isAuthenticated) {
    // If coming from a protected route, preserve phase/email if available
    const loginState = location.state || {};
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location.pathname,
          phase: loginState.phase || undefined,
          email: loginState.email || undefined,
          error: loginState.error || undefined,
        }}
      />
    );
  }

  return <>{children}</>;
}

export default AuthGuard;
